import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  getTenantId,
} from "@/lib/storeManager";
import { refreshAuthTokens } from "@/lib/refreshAuthTokens";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  // Don't add Authorization header for refresh token requests
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  } else {
    console.warn(
      "[axiosConfig] No tenantId available — X-Tenant-Id header will be missing from this request"
    );
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/** Message for user-facing toast, or null when API gave no message (do not toast). */
const getAxiosErrorMessage = (error: any): string | null => {
  const res = error?.response;
  const data = res?.data;

  if (data === null || data === undefined) {
    const m = error?.message;
    return typeof m === "string" && m.trim() ? m.trim() : null;
  }

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message.trim();
  }

  if (data?.detail !== null && data?.detail !== undefined) {
    const d = String(data.detail).trim();
    return d || null;
  }

  const err = data?.error;
  if (typeof err === "string" && err.trim()) {
    return err.trim();
  }
  if (err && typeof err === "object" && typeof err.message === "string") {
    const m = err.message.trim();
    return m ?? null;
  }

  if (Array.isArray(data?.non_field_errors) && data.non_field_errors.length) {
    return data.non_field_errors.map(String).join(", ").trim() ?? null;
  }

  // e.g. { "success": false } with no message — do not show a generic toast
  if (data && typeof data === "object" && data.success === false) {
    return null;
  }

  return null;
};

const notifyErrorIfMessage = (error: any) => {
  const requestUrl = String(error?.config?.url ?? "");
  if (hidenMessageAPI.some(path => requestUrl.includes(path))) {
    return;
  }
  const msg = getAxiosErrorMessage(error);
  if (msg) {
    notify.error(msg);
  }
};

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      notifyErrorIfMessage(error);
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshAuthTokens();
        const newAccessToken = response.access;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      notifyErrorIfMessage(error);
      return Promise.reject(error);
    }
  }
);
